import {
  ArrowLeftIcon,
  ExternalLinkIcon,
  LifeBuoyIcon,
  ShieldCheckIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { Platform, ScrollView, Switch, View } from 'react-native';

import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@sixam/ui/card';
import { Icon } from '@sixam/ui/icon';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Text } from '@sixam/ui/text';
import { BRAND } from '@sixam/config/brand';

export type AccountSettingsScreenProps = Readonly<{
  emailChange: string;
  error?: string | null;
  isSaving: boolean;
  isDeleting: boolean;
  message?: string | null;
  onBack: () => void;
  onEmailChange: (value: string) => void;
  onDeleteAccount: () => void;
  onPrivacy: () => void;
  onPhoneChange: (value: string) => void;
  onPhoneIsPublicChange: (value: boolean) => void;
  onSave: () => void;
  onSupport: () => void;
  phone: string;
  phoneIsPublic: boolean;
  phoneVerified: boolean;
  topPadding?: number;
  userEmail?: string | null;
}>;

export function AccountSettingsScreen({
  emailChange,
  error,
  isSaving,
  isDeleting,
  message,
  onBack,
  onEmailChange,
  onDeleteAccount,
  onPrivacy,
  onPhoneChange,
  onPhoneIsPublicChange,
  onSave,
  onSupport,
  phone,
  phoneIsPublic,
  phoneVerified,
  topPadding = 52,
  userEmail,
}: AccountSettingsScreenProps) {
  return (
    <ScrollView
      className="flex-1 bg-background"
      contentContainerClassName="pb-28"
      contentInsetAdjustmentBehavior="automatic"
    >
      <View
        className="gap-4 px-4 pb-6 sm:px-6"
        style={{ backgroundColor: BRAND.color.ink, paddingTop: topPadding }}
      >
        {Platform.OS === 'web' ? (
          <Button accessibilityLabel="Back" onPress={onBack} size="icon" variant="outline">
            <Icon as={ArrowLeftIcon} className="size-5 text-white" />
          </Button>
        ) : null}
        <View className="gap-2">
          {Platform.OS === 'web' ? (
            <Text variant="h2" className="text-white">
              Account
            </Text>
          ) : null}
          {userEmail ? (
            <Text className="text-white/80" numberOfLines={2} selectable>
              {userEmail}
            </Text>
          ) : null}
        </View>
      </View>

      <View className="gap-4 bg-background px-4 pt-4 sm:px-6">
        {message ? <Text variant="muted">{message}</Text> : null}
        {error ? (
          <Text className="text-sm text-destructive" selectable>
            {error}
          </Text>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardContent className="gap-4">
            <View className="gap-1.5">
              <Label>Email</Label>
              <Input
                value={emailChange}
                onChangeText={onEmailChange}
                placeholder={userEmail ?? 'runner@6ambkk.com'}
                autoCapitalize="none"
                keyboardType="email-address"
                textContentType="emailAddress"
              />
            </View>
            <View className="gap-1.5">
              <Label>Phone</Label>
              <Input
                value={phone}
                onChangeText={onPhoneChange}
                placeholder="+66 81 234 5678"
                keyboardType="phone-pad"
                textContentType="telephoneNumber"
              />
              <Text variant="muted">
                {phoneVerified ? 'Verified phone' : 'Phone not verified yet'}
              </Text>
            </View>
            <View className="flex-row items-center justify-between gap-4">
              <View className="min-w-0 flex-1">
                <Text variant="large">Public phone</Text>
                <Text variant="muted">Show this verified number on your member profile.</Text>
              </View>
              <Switch value={phoneIsPublic} onValueChange={onPhoneIsPublicChange} />
            </View>
            <Button disabled={isSaving || isDeleting} onPress={onSave}>
              <Text>{isSaving ? 'Saving...' : 'Save account'}</Text>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Privacy and support</CardTitle>
          </CardHeader>
          <CardContent className="gap-3">
            <Button onPress={onPrivacy} variant="secondary">
              <Icon as={ShieldCheckIcon} className="size-5 text-white" />
              <Text>Privacy policy</Text>
              <Icon as={ExternalLinkIcon} className="size-4 text-white" />
            </Button>
            <Button onPress={onSupport} variant="secondary">
              <Icon as={LifeBuoyIcon} className="size-5 text-white" />
              <Text>Support</Text>
              <Icon as={ExternalLinkIcon} className="size-4 text-white" />
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Delete account</CardTitle>
          </CardHeader>
          <CardContent className="gap-3">
            <Text variant="muted">
              Permanently delete your profile, memberships, registrations, contacts, connected
              services, notification tokens, and uploaded account data. This cannot be undone.
            </Text>
            <Button
              accessibilityLabel="Permanently delete account"
              disabled={isDeleting || isSaving}
              onPress={onDeleteAccount}
              variant="destructive"
            >
              <Text>{isDeleting ? 'Deleting account...' : 'Delete account'}</Text>
            </Button>
          </CardContent>
        </Card>
      </View>
    </ScrollView>
  );
}
